<script lang="ts">
    import type {
        SchedularEmployee,
        View
    } from '../types';
    import type {PageStateAny} from "../state";
    import { SvelteMap } from 'svelte/reactivity';
    import { toast } from 'svelte-sonner';
    import { GripVertical } from 'lucide-svelte';
    type Props = {
        employee: SchedularEmployee;
        view: View;
        pageState: PageStateAny;
        assignEmployeesContainer: HTMLDivElement  | undefined
        employeesContainer: HTMLDivElement  | undefined
        employeesMap: SvelteMap<string, SchedularEmployee>;
    };
    let {
        employee,
        view,
        pageState,
        assignEmployeesContainer,
        employeesContainer,
        employeesMap,
    }: Props = $props();
    let node: HTMLDivElement;
    const draggingNode = document.createElement("div");
    draggingNode.classList.add("absence");
    //let duration = absence.duration_hours * 3600;

    const onMouseDownPos = (e: MouseEvent) => {
        if (e.buttons === 2) {
            return;
        } else if (e.buttons !== 1) {
            return;
        }
        document.getSelection()?.empty();
        pageState.selectedEmployee = {
            type: "employee",
            item: employee,
            absence: null,
            dragging: null,
        };
       // console.log("onMouseDownPos", employee.id, pageState.selected);
    };

    let draggingEmployee = employee;
    let ghostEl: HTMLElement | null = null;

    function createGhost(clientX: number, clientY: number) {
        ghostEl = document.createElement("div");
        ghostEl.className = "drag-ghost";
        ghostEl.textContent = employee.name;
        ghostEl.style.position = "fixed";
        ghostEl.style.pointerEvents = "none";
        ghostEl.style.background = `hsl(${hue} ${saturation}% var(--scheduler-job-light))`;
        ghostEl.style.color = "hsl(var(--clr-on-surface-1))";
        ghostEl.style.padding = "8px 12px";
        ghostEl.style.border = "1px solid #00000070";
        ghostEl.style.borderRadius = "5px";
        ghostEl.style.opacity = "0.85";
        ghostEl.style.zIndex = "1000";
        ghostEl.style.boxShadow = "0 0 2rem 0 #00000070";
        ghostEl.style.left = clientX + "px";
        ghostEl.style.top = clientY + "px";
        const ghostContainer = (document.fullscreenElement as HTMLElement) ?? document.body;
        ghostContainer.appendChild(ghostEl);
    }

    function moveGhost(clientX: number, clientY: number) {
        if (ghostEl) {
            ghostEl.style.left = clientX + "px";
            ghostEl.style.top = clientY + "px";
        }
    }

    function handleDrop(clientX: number, clientY: number) {
        if (ghostEl) ghostEl.remove();
        ghostEl = null;

        if (!draggingEmployee || !pageState.selected) return;

        const assignRect = assignEmployeesContainer?.getBoundingClientRect();
        const employeesRect = employeesContainer?.getBoundingClientRect();

        if (
            assignRect &&
            clientX >= assignRect.left &&
            clientX <= assignRect.right &&
            clientY >= assignRect.top &&
            clientY <= assignRect.bottom
        ) {
            if (!pageState.selected) return;
            let newEmployeeAssignment = {
                id: getUUID(),
                employee_id: draggingEmployee.id,
                name: draggingEmployee.name,
                start_date_time: pageState.selected?.item.start,
                end_date_time: pageState.selected?.item.end,
                employee: draggingEmployee,
                assignable_id: pageState.selected?.item.id,
                assignable_type: pageState.selected?.item.visitable_type ? 'App\\Models\\SiteVisit' : 'App\\Models\\VehicleAssignment',
                deleted: false,
            };
            let existing = false;
            for (let i = 0; i < pageState.selected.item.employeeAssignments.length; i++) {
                const assignment = pageState.selected.item.employeeAssignments[i];
                if (assignment.employee_id === draggingEmployee.id) {
                    existing = true;
                    if (assignment.deleted) {
                        pageState.selected.item.employeeAssignments[i] = {
                            ...assignment,
                            start_date_time: pageState.selected?.item.start,
                            end_date_time: pageState.selected?.item.end,
                            deleted: false,
                        };
                    } else {
                        toast.error(`Employee ${draggingEmployee.name} is already assigned to this assignment.`);
                    }
                    break;
                }
            }
            if (!existing) pageState.selected.item.employeeAssignments.push(newEmployeeAssignment);
            pageState.selected.item.modified = true;
            employeesMap.delete(draggingEmployee.id);
        } else if (
            employeesRect &&
            clientX >= employeesRect.left &&
            clientX <= employeesRect.right &&
            clientY >= employeesRect.top &&
            clientY <= employeesRect.bottom
        ) {
            employeesMap.set(draggingEmployee.id, draggingEmployee);
        }

        draggingEmployee = null;
    }

    function onEmployeeMouseDown(e: MouseEvent) {
        pageState.selectedEmployee = { type: "employee", item: employee, absence: null, dragging: null };
        e.preventDefault();
        draggingEmployee = employee;
        createGhost(e.clientX, e.clientY);

        function onMouseMove(moveEvent: MouseEvent) { moveGhost(moveEvent.clientX, moveEvent.clientY); }
        function onMouseUp(upEvent: MouseEvent) {
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
            handleDrop(upEvent.clientX, upEvent.clientY);
        }
        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
    }

    function onEmployeeTouchStart(e: TouchEvent) {
        const touch = e.touches[0];
        if (!touch) return;
        pageState.selectedEmployee = { type: "employee", item: employee, absence: null, dragging: null };
        e.preventDefault();
        draggingEmployee = employee;
        createGhost(touch.clientX, touch.clientY);

        function onTouchMove(moveEvent: TouchEvent) {
            moveEvent.preventDefault();
            const t = moveEvent.touches[0];
            if (t) moveGhost(t.clientX, t.clientY);
        }
        function onTouchEnd(upEvent: TouchEvent) {
            document.removeEventListener("touchmove", onTouchMove);
            document.removeEventListener("touchend", onTouchEnd);
            const t = upEvent.changedTouches[0];
            if (t) handleDrop(t.clientX, t.clientY);
        }
        document.addEventListener("touchmove", onTouchMove, { passive: false });
        document.addEventListener("touchend", onTouchEnd);
    }

    function getUUID(): string {
        if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
            return crypto.randomUUID();
        }
        // fallback RFC4122 v4
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }


    // Calculate hue and saturation based on availability
    let hue = $derived(
        employee.free_percent === 100
            ? 120  // Green
            : employee.free_percent > 50
                ? 50   // Yellow
                : 0    // Red
    );

    let saturation = $derived(
        employee.free_percent === 100
            ? 60
            : employee.free_percent > 50
                ? 80
                : 70
    );

</script>

<div
    role="none"
    class="employee"
    bind:this={node}
    style:--hue={`${hue}`}
    style:--sat={`${saturation}%`}
    style:--light="var(--scheduler-job-light)"
    data-id={employee.id}
    data-name={employee.name}
    onmousedown={onEmployeeMouseDown}
    ontouchstart={onEmployeeTouchStart}
    oncontextmenu={(e) => {
        e.preventDefault();
        return false;
    }}
    ondragstart={() => false}
>
    <div class="content">
        <GripVertical class="grip-icon" />
        <p class="employee-name">{employee.name}</p>
    </div>
</div>

<style lang="scss">
    .employee {
        opacity: 1;
        border-radius: 5px;
        background: hsl(var(--hue) var(--sat) var(--light));
        color: hsl(var(--clr-on-surface-1));
        width: var(--width);
        height: var(--height);
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        padding: 0.5rem;
        z-index: 10;
        left: var(--left);
        top: var(--top);
        outline: solid 2px transparent;
        transition: outline 200ms, box-shadow 200ms, opacity 200ms, background 200ms;
        position: relative;
        border: 1px solid #00000070;
        cursor: grab;

        &:hover {
            opacity: 1;
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
        }

        &:active {
            cursor: grabbing;
        }

        .content {
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 0.5rem;
            width: 100%;
        }

        :global(.grip-icon) {
            width: 16px;
            height: 16px;
            opacity: 0.5;
            flex-shrink: 0;
        }

        .employee-name {
            font-size: 0.875rem;
            font-weight: 500;
            margin: 0;
            text-align: center;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            width: 100%;
        }
    }
</style>
